/**
 * Inputs: slide children + controlled index.
 * Outputs: native scroll-snap deck with previous/next, dots, and keyboard arrows.
 * Programmatic selection is not overwritten by the visibility observer.
 */

import { Children, useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";

export function SwipeDeck({
  children,
  index,
  onIndexChange,
  label,
  dock,
}: {
  children: ReactNode;
  index: number;
  onIndexChange: (index: number) => void;
  label: string;
  dock?: ReactNode;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const slides = Children.toArray(children);
  const count = slides.length;
  const safeIndex = count ? ((index % count) + count) % count : 0;
  const indexRef = useRef(safeIndex);
  const onIndexChangeRef = useRef(onIndexChange);
  const programmaticRef = useRef(false);
  indexRef.current = safeIndex;
  onIndexChangeRef.current = onIndexChange;

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.children[safeIndex] as HTMLElement | undefined;
    if (!slide) return;
    programmaticRef.current = true;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    slide.scrollIntoView({ behavior: reduce ? "auto" : "smooth", inline: "start", block: "nearest" });
    const done = () => {
      programmaticRef.current = false;
    };
    track.addEventListener("scrollend", done, { once: true });
    const timer = window.setTimeout(done, 500);
    return () => {
      window.clearTimeout(timer);
      track.removeEventListener("scrollend", done);
    };
  }, [safeIndex]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (programmaticRef.current) return;
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const next = Array.from(track.children).indexOf(visible.target);
        if (next < 0 || next === indexRef.current) return;
        onIndexChangeRef.current(next);
      },
      { root: track, threshold: 0.55 },
    );
    for (const child of track.children) observer.observe(child);
    return () => observer.disconnect();
  }, [count]);

  if (!count) return null;

  function onKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      onIndexChange((safeIndex + 1) % count);
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      onIndexChange((safeIndex + count - 1) % count);
    }
  }

  return (
    <div className="swipe-deck" aria-label={label}>
      <div
        ref={trackRef}
        className="swipe-track"
        tabIndex={0}
        onKeyDown={onKey}
      >
        {slides.map((slide, i) => (
          <div key={i} className="swipe-slide">
            {slide}
          </div>
        ))}
      </div>
      {dock ? <div className="swipe-dock">{dock}</div> : null}
      <div className="swipe-controls">
        <button
          className="btn-ghost"
          type="button"
          onClick={() => onIndexChange((safeIndex + count - 1) % count)}
        >
          Previous
        </button>
        <div className="swipe-dots" aria-hidden="true">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`swipe-dot ${i === safeIndex ? "active" : ""}`}
              onClick={() => onIndexChange(i)}
              tabIndex={-1}
            />
          ))}
        </div>
        <button
          className="btn-ghost"
          type="button"
          onClick={() => onIndexChange((safeIndex + 1) % count)}
        >
          Next
        </button>
      </div>
      <p className="muted tabular" data-deck-count={`${safeIndex + 1} of ${count}`} data-deck-label={label}>{safeIndex + 1} of {count}</p>
    </div>
  );
}
